/**
 * Authentication Error Reporting Service
 * Centralized error reporting and analytics for auth system
 */

interface ErrorReport {
  id: string;
  timestamp: Date;
  error: {
    name: string;
    message: string;
    stack?: string;
  };
  context: {
    component: string;
    action: string;
    userId?: number;
    sessionId?: string;
    url: string;
    userAgent: string;
    authToken: boolean;
  };
  metadata: {
    retryCount: number;
    recoveryAttempted: boolean;
    recoveryType?: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  };
  environment: {
    nodeEnv: string;
    buildVersion?: string;
    browserInfo: {
      name: string;
      version: string;
      os: string;
    };
  };
}

interface ErrorMetrics {
  totalErrors: number;
  errorsByType: Record<string, number>;
  errorsByComponent: Record<string, number>;
  errorsByUser: Record<string, number>;
  recoverySuccessRate: number;
  averageRetryCount: number;
}

export class AuthErrorReportingService {
  private static instance: AuthErrorReportingService;
  private errorQueue: ErrorReport[] = [];
  private metrics: ErrorMetrics = {
    totalErrors: 0,
    errorsByType: {},
    errorsByComponent: {},
    errorsByUser: {},
    recoverySuccessRate: 0,
    averageRetryCount: 0,
  };
  private isOnline = navigator.onLine;
  private maxQueueSize = 100;
  private flushInterval = 30000; // 30 seconds
  private flushTimer?: NodeJS.Timeout;

  private constructor() {
    this.initializeService();
  }

  public static getInstance(): AuthErrorReportingService {
    if (!AuthErrorReportingService.instance) {
      AuthErrorReportingService.instance = new AuthErrorReportingService();
    }
    return AuthErrorReportingService.instance;
  }

  private initializeService(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushErrorQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Start periodic flush
    this.startFlushTimer();

    // Load persisted errors from localStorage on startup
    this.loadPersistedErrors();
  }

  public reportError(
    error: Error,
    component: string,
    action: string,
    metadata: Partial<ErrorReport['metadata']> = {}
  ): string {
    const errorReport: ErrorReport = {
      id: this.generateErrorId(),
      timestamp: new Date(),
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      context: {
        component,
        action,
        userId: this.getCurrentUserId(),
        sessionId: this.getSessionId(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        authToken: !!localStorage.getItem('auth_token'),
      },
      metadata: {
        retryCount: 0,
        recoveryAttempted: false,
        severity: 'medium',
        ...metadata,
      },
      environment: {
        nodeEnv: process.env.NODE_ENV || 'production',
        buildVersion: process.env.REACT_APP_VERSION,
        browserInfo: this.getBrowserInfo(),
      },
    };

    // Add to queue
    this.addToQueue(errorReport);

    // Update metrics
    this.updateMetrics(errorReport);

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      this.logErrorReport(errorReport);
    }

    // Immediate flush for critical errors
    if (metadata.severity === 'critical') {
      this.flushErrorQueue();
    }

    return errorReport.id;
  }

  public reportRecoveryAttempt(
    errorId: string,
    recoveryType: string,
    success: boolean
  ): void {
    const errorReport = this.errorQueue.find(report => report.id === errorId);
    if (errorReport) {
      errorReport.metadata.recoveryAttempted = true;
      errorReport.metadata.recoveryType = recoveryType;
      errorReport.metadata.retryCount += 1;

      // Update recovery metrics
      this.updateRecoveryMetrics(success);
    }
  }

  public getMetrics(): ErrorMetrics {
    return { ...this.metrics };
  }

  public clearMetrics(): void {
    this.metrics = {
      totalErrors: 0,
      errorsByType: {},
      errorsByComponent: {},
      errorsByUser: {},
      recoverySuccessRate: 0,
      averageRetryCount: 0,
    };
  }

  private addToQueue(errorReport: ErrorReport): void {
    this.errorQueue.push(errorReport);

    // Trim queue if too large
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue = this.errorQueue.slice(-this.maxQueueSize);
    }

    // Persist to localStorage
    this.persistErrors();

    // Try immediate flush if online
    if (this.isOnline && this.errorQueue.length >= 10) {
      this.flushErrorQueue();
    }
  }

  private async flushErrorQueue(): Promise<void> {
    if (!this.isOnline || this.errorQueue.length === 0) {
      return;
    }

    const errorsToFlush = [...this.errorQueue];
    this.errorQueue = [];

    try {
      await this.sendErrorsToBackend(errorsToFlush);
      
      // Clear persisted errors on successful flush
      localStorage.removeItem('auth_error_queue');
      
      console.log(`Successfully flushed ${errorsToFlush.length} error reports`);
    } catch (flushError) {
      console.error('Failed to flush error reports:', flushError);
      
      // Re-add errors to queue on failure
      this.errorQueue.unshift(...errorsToFlush);
      
      // Persist failed flush
      this.persistErrors();
    }
  }

  private async sendErrorsToBackend(errors: ErrorReport[]): Promise<void> {
    const endpoint = '/api/auth/errors';
    
    // エラーレポートエンドポイントが実装されるまで一時的に無効化
    if (process.env.NODE_ENV === 'production') {
      console.log('Error reporting skipped - endpoint not yet implemented');
      return;
    }
    
    const payload = {
      errors: errors.map(error => ({
        ...error,
        timestamp: error.timestamp.toISOString(),
      })),
      clientInfo: {
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        cookiesEnabled: navigator.cookieEnabled,
        localStorage: typeof Storage !== 'undefined',
      },
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) {
        // エラーレポート失敗をコンソールに記録するが、エラーをthrowしない
        console.warn(`Failed to send error reports: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      // ネットワークエラーなどをキャッチしてログに記録
      console.warn('Error reporting failed:', error);
    }
  }

  private updateMetrics(errorReport: ErrorReport): void {
    this.metrics.totalErrors += 1;
    
    // Update error by type
    const errorType = errorReport.error.name;
    this.metrics.errorsByType[errorType] = (this.metrics.errorsByType[errorType] || 0) + 1;
    
    // Update error by component
    const component = errorReport.context.component;
    this.metrics.errorsByComponent[component] = (this.metrics.errorsByComponent[component] || 0) + 1;
    
    // Update error by user
    if (errorReport.context.userId) {
      const userId = errorReport.context.userId.toString();
      this.metrics.errorsByUser[userId] = (this.metrics.errorsByUser[userId] || 0) + 1;
    }
  }

  private updateRecoveryMetrics(success: boolean): void {
    const totalRecoveryAttempts = Object.values(this.metrics.errorsByType).reduce((sum, count) => sum + count, 0);
    if (totalRecoveryAttempts === 0) return;

    // This is a simplified calculation - in reality you'd track successes separately
    if (success) {
      this.metrics.recoverySuccessRate = Math.min(
        (this.metrics.recoverySuccessRate * (totalRecoveryAttempts - 1) + 100) / totalRecoveryAttempts,
        100
      );
    }
  }

  private persistErrors(): void {
    try {
      const serializedErrors = JSON.stringify(
        this.errorQueue.map(error => ({
          ...error,
          timestamp: error.timestamp.toISOString(),
        }))
      );
      localStorage.setItem('auth_error_queue', serializedErrors);
    } catch (e) {
      console.warn('Failed to persist error queue:', e);
    }
  }

  private loadPersistedErrors(): void {
    try {
      const serializedErrors = localStorage.getItem('auth_error_queue');
      if (serializedErrors) {
        const errors = JSON.parse(serializedErrors);
        this.errorQueue = errors.map((error: any) => ({
          ...error,
          timestamp: new Date(error.timestamp),
        }));
        
        console.log(`Loaded ${this.errorQueue.length} persisted error reports`);
      }
    } catch (e) {
      console.warn('Failed to load persisted errors:', e);
      localStorage.removeItem('auth_error_queue');
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flushErrorQueue();
    }, this.flushInterval);
  }

  private generateErrorId(): string {
    return `auth_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private getCurrentUserId(): number | undefined {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return undefined;
      
      // Decode JWT to get user ID (basic implementation)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.user_id;
    } catch (e) {
      return undefined;
    }
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  private getBrowserInfo(): ErrorReport['environment']['browserInfo'] {
    const userAgent = navigator.userAgent;
    
    // Simple browser detection
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';
    let os = 'Unknown';

    if (userAgent.includes('Chrome')) {
      browserName = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Safari')) {
      browserName = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    }

    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';

    return { name: browserName, version: browserVersion, os };
  }

  private logErrorReport(errorReport: ErrorReport): void {
    console.group(`📊 Error Report - ${errorReport.id}`);
    console.table({
      'Error Type': errorReport.error.name,
      'Message': errorReport.error.message,
      'Component': errorReport.context.component,
      'Action': errorReport.context.action,
      'Severity': errorReport.metadata.severity,
      'Retry Count': errorReport.metadata.retryCount,
      'Recovery Attempted': errorReport.metadata.recoveryAttempted,
    });
    if (errorReport.error.stack) {
      console.error('Stack trace:', errorReport.error.stack);
    }
    console.groupEnd();
  }

  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    
    // Final flush
    this.flushErrorQueue();
  }
}

// Export singleton instance
export const authErrorReporting = AuthErrorReportingService.getInstance();

// Export types for external use
export type { ErrorReport, ErrorMetrics };